<template>
    <card>

        <template #header>
            <div class="d-flex align-items-center justify-content-between">
                <h1 class="card-title">Doctor Follow-Ups</h1>

                <div>
                    <b-form-group>
                        <b-form-radio-group v-model="selectedMode" :options="['Queue', 'All']" button-variant="primary"
                            buttons />
                    </b-form-group>
                </div>
            </div>
        </template>

        <div class="filter-grid">
            <div>
                <SearchComponent :key="selectedDoctor?.id ?? 0" v-model="selectedDoctor" label="Doctor"
                    search-u-r-l="/Prescriber" clearable />
            </div>

            <div>
                <SearchComponent v-model="selectedProgram" label="Program"
                    search-u-r-l="/Program" :params="{ active: true }" prepopulate clearable />
            </div>

            <EnumSelector v-model="selectedFaxType" label="Request Type" :enum-codes="faxTypeEnum"
                :elementsToDiscard="[faxTypeEnum.RxExternalTransfer]" clearable />

            <div>
                <label>Created Date:</label>
                <div class="d-flex" style="gap: 15px;align-items: center;margin-top: -7px;">
                    <el-date-picker size="sm" v-model="startDate" today-button reset-button :disabled="isLoading" />
                    <el-date-picker size="sm" v-model="endDate" today-button reset-button :disabled="isLoading" />

                    <b-dropdown size="sm" right>
                        <b-dropdown-item @click="createdToday">Today</b-dropdown-item>
                        <b-dropdown-item @click="createdYesterday">Yesterday</b-dropdown-item>
                        <b-dropdown-item @click="createdThisWeek">This Week</b-dropdown-item>
                        <b-dropdown-item @click="createdLastWeek">Last Week</b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="startDate = ''; endDate = '';">Clear</b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>

            <div>
                <checkbox v-model="includeUpcoming">Include Scheduled</checkbox>
            </div>
        </div>

        <div v-if="selectedPhone">
            <b-button pill variant="primary" @click="selectedPhone = null">
                {{ selectedPhone }}
                <i class="fas fa-times-circle" />
            </b-button>
        </div>

        <template v-if="!isLoading">
            <b-table class="mt-3" :items="filteredItems" :fields="fields" :busy="isLoading" :sort-by.sync="sortBy"
                :per-page="pageSize" :current-page="pageNumber" @sort-changed="pageNumber = 1" empty-text="No results."
                show-empty bordered striped>
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle" />
                        <strong>Loading...</strong>
                    </div>
                </template>
                <template #cell(statusName)="data">
                    {{ data.value }}
                    <br />
                    {{ relativeFormat(data.item.statusUpdated ?? data.item.created) }}
                </template>
                <template #cell(created)="data">
                    <span v-b-tooltip.hover :title='`Created by ${data.item.createdBy}`'>
                        {{ relativeFormat(data.value) }}
                    </span>
                </template>
                <template #cell(requestName)="data">
                    <div class="d-flex justify-content-between align-items-center">
                        <span v-b-tooltip.hover :title="!!data.item.rxID ? `RxID: ${data.item.rxID}` : ''">
                            {{ data.value }}
                        </span>

                        <b-link @click="filterByType(data.item.type)" class="ml-2">
                            <b-icon icon="search" />
                        </b-link>
                    </div>
                </template>
                <template #cell(updated)="data">
                    <span v-b-tooltip.hover :title='`Updated by ${data.item.updatedBy}`'>
                        {{ relativeFormat(data.value) }}
                    </span>
                </template>
                <template #cell(actions)="data">
                    <div class="d-flex flex-column" style="gap: 6px;">
                        <b-button block variant="primary" size="sm" @click.prevent="openDetails(data.item)"
                            class="mb-0">
                            <i class="fa fa-pencil"></i>
                            Edit
                        </b-button>
                        <template v-if="filteredAvailableCustomTextTemplates.length">
                            <b-button block variant="secondary" size="sm"
                                :id="`custom-text-msg-action-item-${data.item.id}`" @click.prevent
                                style="padding: 5px 3px;">
                                <i class="fa fa-comment"></i>
                                Send SMS
                            </b-button>

                            <b-popover :target="`custom-text-msg-action-item-${data.item.id}`" triggers="click blur"
                                custom-class="actions-popover glass-pane sub" placement="righttop"
                                @show="showingCustomTextSub = true" @hide="showingCustomTextSub = false">
                                <h6>Custom Text Messages</h6>
                                <ul>
                                    <b-dropdown-item v-for="t in filteredAvailableCustomTextTemplates" :key="t.id"
                                        @mousedown.native.stop.prevent="startCustomTextSend(t, data)">
                                        <div style="white-space: normal;">
                                            {{ t.name }}
                                        </div>
                                    </b-dropdown-item>
                                </ul>
                            </b-popover>
                        </template>
                    </div>
                </template>
                <template #cell(prescriberName)="data">
                    <div class="d-flex justify-content-between align-items-center">
                        {{ data.value }}
                        <b-link @click="filterByPrescriber(data.item.prescriberId)" class="ml-2">
                            <b-icon icon="search" />
                        </b-link>
                    </div>
                </template>
                <template #cell(prescriberPhone)="data">
                    {{ data.value }}
                    <b-link @click="filterByPhone(data.value)" class="ml-2">
                        <b-icon icon="search" />
                    </b-link>
                </template>
            </b-table>
        </template>

        <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize" show-total
            show-page-size-selector @change-page-size="pageSize = $event"
            :loading="isLoading" />

        <b-modal :title="activeCustomTextTemplate?.name" :visible="!!activeCustomTextTemplate" no-fade
            ok-title="Send Text" @ok="sendCustomText" @hidden="activeCustomTextTemplate = null">
            <CustomTextMessageTemplateSend v-if="activeCustomTextTemplate" v-model="activeCustomTextTemplate"
                :patient="patient" />
        </b-modal>

        <!-- Follow-up case details -->
        <b-modal :id="caseModalId" title="Doctor Follow-Up Case" size="xl" hide-footer>

            <!-- FOLLOW UP COMPONENT -->
            <DoctorFollowUpCaseForm v-model="selectedCase" @submitted="closeCaseModalAndReload" />

        </b-modal>
    </card>
</template>
<script lang="ts">
import Vue from 'vue';
import { PrescriberFollowUpCase, FaxType } from '@/models/PrescriberFollowUpCase';
import { Prescriber } from '@/models/Prescriber';
import { Program } from '@/models/Program';
import { Component, Watch } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import DoctorFollowUpCaseForm from '@/components/Doctor/DoctorFollowUpCaseForm.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import { DatePicker } from "element-ui";
import moment from 'moment';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { NoteActivityType } from "@/models/Note";
import { CustomTextMessageTemplate } from "@/models/CustomTextMessageTemplate";
import { Store } from "@/models/Store";
import { Prescription } from "@/models/Prescription";
import { BvModalEvent } from "bootstrap-vue";
import axios from "axios";
import CustomTextMessageTemplateSend from "@/components/Patient/CustomTextMessageTemplateSend.vue";
import { Patient } from "@/models/Patient";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { saveQueueFilters, loadQueueFilters } from '@/util/queueFilterPersistence';

@Component({
    name: "DoctorFollowUpQueue",
    components: {
        CustomTextMessageTemplateSend,
        DoctorFollowUpCaseForm,
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        Checkbox,
        BottomPagerBar,
    },
})
export default class DoctorFollowUpQueue extends Vue {
    private caseModalId = 'followup-case-form';
    private isLoading = false;
    private items: Array<PrescriberFollowUpCase> = new Array<PrescriberFollowUpCase>();
    private selectedCase: PrescriberFollowUpCase = new PrescriberFollowUpCase();
    private selectedDoctor: Prescriber = new Prescriber();
    private selectedProgram: Program = new Program();
    private selectedFaxType: FaxType | null = null;
    private selectedPhone: string | null = null;
    protected faxTypeEnum = FaxType;
    protected startDate?: string = "";
    protected endDate?: string = "";
    protected selectedMode: string = "Queue";
    protected outreachType = NoteActivityType;
    protected includeUpcoming = false;

    private fields: any[] = [
        {
            key: "patientName",
            label: "Patient",
            sortable: true,
        },
        {
            key: "requestName",
            label: "Request",
            sortable: true,
        },
        {
            key: "outreachType",
            label: "Outreach Type",
            sortable: true,
            formatter: (value: number) => value !== null ? this.outreachType[value] : 'Fax',
        },
        {
            key: "statusName",
            label: "Status",
            sortable: true,
        },
        {
            key: "prescriberName",
            label: "Prescriber Name",
            sortable: true,
        },
        {
            key: "prescriberPhone",
            sortable: true,
        },
        {
            key: "created",
            label: "Created",
            sortable: true,
        },
        {
            key: "updated",
            label: "Last Activity",
            sortable: true,
        },
        {
            key: "nextTouchDate",
            label: "Next Touch",
            formatter: (v: any) => v ? moment.utc(v).local().format("l LT") : "—",
            sortable: true,
        },
        {
            key: "actions",
            label: '',
        },
    ];
    private sortBy: string = 'created';

    private pageSize = 50;
    private pageNumber = 1;

    @Watch("pageSize")
    resetPageOnSizeChange() {
        this.pageNumber = 1;
        savePageSizePreference(this, 'pageSize_DoctorFollowUp', this.pageSize);
    }

    get totalRows() {
        return this.filteredItems.length;
    }

    @Watch("filteredItems")
    resetPaging() {
        this.pageNumber = 1;
    }

    private skipFilterSave = false;

    created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_DoctorFollowUp', 50);
        this.restoreFilters();
        this.getAvailableCustomTextTemplates();
    }

    private restoreFilters() {
        const saved = loadQueueFilters(this, 'doctorFollowUpQueue_filters');
        this.skipFilterSave = true;
        if (saved) {
            if (typeof saved.selectedMode === 'string') this.selectedMode = saved.selectedMode;
            if (typeof saved.includeUpcoming === 'boolean') this.includeUpcoming = saved.includeUpcoming;
            if (saved.selectedFaxType !== undefined) this.selectedFaxType = saved.selectedFaxType;
            if (saved.selectedDoctor?.id) this.selectedDoctor = new Prescriber(saved.selectedDoctor);
            if (saved.selectedProgram?.id) this.selectedProgram = new Program(saved.selectedProgram);
            if (typeof saved.startDate === 'string') this.startDate = saved.startDate;
            if (typeof saved.endDate === 'string') this.endDate = saved.endDate;
        }
        this.$nextTick(() => {
            this.skipFilterSave = false;
            this.loadItems();
        });
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveQueueFilters(this, 'doctorFollowUpQueue_filters', {
            selectedMode: this.selectedMode,
            includeUpcoming: this.includeUpcoming,
            selectedFaxType: this.selectedFaxType,
            selectedDoctor: this.selectedDoctor?.id
                ? { id: this.selectedDoctor.id, lastName: this.selectedDoctor.lastName, firstName: this.selectedDoctor.firstName }
                : null,
            selectedProgram: this.selectedProgram?.id
                ? { id: this.selectedProgram.id, name: this.selectedProgram.name, abbreviation: this.selectedProgram.abbreviation }
                : null,
            startDate: this.startDate ?? '',
            endDate: this.endDate ?? '',
        });
    }

    @Watch("startDate")
    @Watch("endDate")
    private persistDateChange() {
        this.persistFilters();
    }

    get filteredItems() {
        return this.items.filter(this.startDateFilter).filter(this.doctorPhoneFilter);
    }

    doctorPhoneFilter(i: PrescriberFollowUpCase): boolean {
        if (!this.selectedPhone) return true;
        return i.prescriberPhone == this.selectedPhone;
    }

    startDateFilter(i: PrescriberFollowUpCase): boolean {
        if (!this.startDate) return true;
        return new Date(i.created) >= moment(this.startDate).toDate() &&
            new Date(i.created) < moment(this.endDate).add(1, "day").toDate();
    }

    @Watch("selectedDoctor")
    handleDoctorChange(newValue: Prescriber, oldValue: Prescriber) {
        if (newValue === null) {
            this.selectedDoctor = new Prescriber();
        }
    }

    @Watch("selectedProgram")
    handleProgramChange(newValue: Program | null) {
        if (newValue === null) {
            this.selectedProgram = new Program();
        }
    }

    @Watch("selectedDoctor")
    @Watch("selectedProgram")
    @Watch("selectedFaxType")
    @Watch("selectedMode")
    @Watch("includeUpcoming")
    loadItems() {
        if (this.skipFilterSave) return;
        this.persistFilters();
        this.isLoading = true;
        this.$http.get<PrescriberFollowUpCase[]>(`/Prescriber/follow-up-queue/${this.selectedDoctor?.id || 0}/${this.selectedFaxType || ''}`,
            {
                params: {
                    allStatuses: this.selectedMode === "All",
                    includeUpcoming: this.includeUpcoming,
                    programId: this.selectedProgram?.id || undefined,
                },
            },
        )
            .then(res => {
                this.items = res.data.map(i => new PrescriberFollowUpCase(i));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
                const errorMsg = 'Error while loading prescribers follow-up cases.';
                console.error(errorMsg, {
                    err,
                    response: err?.response,
                });
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    relativeFormat(date: Date) {
        return moment.utc(date).local().fromNow();
    }

    closeCaseModalAndReload() {
        this.$bvModal.hide(this.caseModalId);
        this.loadItems();
    }

    openDetails(selectedItem: PrescriberFollowUpCase) {
        this.selectedCase = selectedItem;
        this.$bvModal.show(this.caseModalId);
    }

    filterByPrescriber(prescriberId: number) {
        const prescriber = new Prescriber();
        prescriber.id = prescriberId;
        this.selectedDoctor = prescriber;
    }

    filterByPhone(phone: string) {
        this.selectedPhone = phone;
    }

    filterByType(type: number) {
        this.selectedFaxType = type;
    }

    @Watch("startDate")
    handleEndDate() {
        if (this.startDate && !this.endDate) {
            this.endDate = moment().toISOString();
        }
        if (!this.startDate) {
            this.endDate = "";
        }
    }

    @Watch("endDate")
    handleStartDate() {
        if (this.endDate && !this.startDate) {
            this.startDate = this.endDate;
        }
        if (!this.endDate) {
            this.startDate = "";
        }
    }

    createdToday() {
        this.startDate = new Date().toDateString();
        this.endDate = new Date().toDateString();
    }

    createdYesterday() {
        this.startDate = moment().subtract(1, 'day').toString();
        this.endDate = moment().subtract(1, 'day').toString();
    }

    createdThisWeek() {
        this.startDate = moment().startOf('week').toString();
        this.endDate = moment().startOf('week').add(1, 'week').toString();
    }

    createdLastWeek() {
        this.startDate = moment().startOf('week').subtract(1, 'week').add(1, 'day').toString();
        this.endDate = moment().startOf('week').subtract(1, 'week').add(1, 'week').toString();
    }

    //Custom Text Messages
    protected showingCustomTextSub = false;
    protected availableCustomTextTemplates: CustomTextMessageTemplate[] = [];
    protected activeCustomTextTemplate: CustomTextMessageTemplate | null = null;
    protected patient: Patient = new Patient();

    async getAvailableCustomTextTemplates() {
        try {
            const resp = await this.$http.get<CustomTextMessageTemplate[]>("/CustomTextMessage/available");
            this.availableCustomTextTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    get filteredAvailableCustomTextTemplates() {
        return this.availableCustomTextTemplates.toSorted((a, b) => a.name.localeCompare(b.name));
    }

    startCustomTextSend(template: CustomTextMessageTemplate, row: any) {
        console.log("row:", row);
        this.patient = new Patient();
        this.patient.id = row.item.patientId;

        this.activeCustomTextTemplate = {
            ...template,
            store: new Store(),
            relatedPrescription: new Prescription(),
        };
    }

    async sendCustomText(e: BvModalEvent) {
        e.preventDefault();
        try {
            if (this.activeCustomTextTemplate!.requiresPrescription && !this.activeCustomTextTemplate!.relatedPrescription?.rxNumber) {
                this.$notification(NotificationOptions.error("You must select a prescription."));
                return;
            }

            if (!this.activeCustomTextTemplate!.requiresPrescription &&
                !this.activeCustomTextTemplate!.relatedPrescription?.rxNumber &&
                !this.activeCustomTextTemplate!.store?.id) {
                this.$notification(NotificationOptions.error("You must select a store or perscription."));
                return;
            }

            await axios.post(`CustomTextMessage/send/${this.patient.id}`, this.activeCustomTextTemplate);
            this.activeCustomTextTemplate = null;
            this.$notification(NotificationOptions.success("Successfully sent text message to patient"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

}
</script>

<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 430px), 1fr));
}

@media (min-width: 1600px) {
    .filter-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
